import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useSarLabAuth } from './SarLabAuthContext';

interface SupportMessage {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  senderRole: 'user' | 'admin';
  timestamp: number;
}

interface SupportTicket {
  id: string;
  odaId: string;
  odaAdi: string;
  userId: string;
  username: string;
  subject: string;
  initialMessage: string;
  status: 'waiting' | 'active' | 'closed';
  createdAt: number;
  assignedAdmin?: string;
  assignedAdminName?: string;
}

interface LiveSupportContextType {
  activeTicket: SupportTicket | null;
  tickets: SupportTicket[];
  messages: SupportMessage[];
  isConnected: boolean;
  isLoadingTicket: boolean;
  createTicket: (subject: string, initialMessage: string) => Promise<string>;
  sendSupportMessage: (text: string) => Promise<void>;
  closeTicket: (saveToHistory?: boolean) => Promise<void>;
  joinTicket: (ticketId: string) => Promise<void>;
  leaveTicket: () => void;
  adminCloseTicket: (ticketId: string) => Promise<void>;
}

const LiveSupportContext = createContext<LiveSupportContextType>({} as LiveSupportContextType);

export const useLiveSupport = () => {
  return useContext(LiveSupportContext);
};

interface LiveSupportProviderProps {
  children: ReactNode;
}

export const LiveSupportProvider: React.FC<LiveSupportProviderProps> = ({ children }) => {
  const { currentPlayer } = useSarLabAuth();
  const [activeTicket] = useState<SupportTicket | null>(null);
  const [tickets] = useState<SupportTicket[]>([]);
  const [messages] = useState<SupportMessage[]>([]);
  const [isLoadingTicket] = useState(false);

  // SarLab live support entegrasyonu için placeholder fonksiyonlar

  const createTicket = async (_subject: string, _initialMessage: string): Promise<string> => {
    if (!currentPlayer) throw new Error('Kullanıcı girişi gerekli');
    // TODO: SarLab live support API entegrasyonu
    console.log('Live support ticket oluşturuluyor (not implemented yet)');
    return '';
  };

  const sendSupportMessage = async (_text: string): Promise<void> => {
    if (!currentPlayer || !activeTicket) return;
    // TODO: SarLab live support API entegrasyonu
    console.log('Support message gönderiliyor (not implemented yet)');
  };

  const closeTicket = async (_saveToHistory: boolean = true): Promise<void> => {
    if (!activeTicket) return;
    // TODO: SarLab live support API entegrasyonu
    console.log('Ticket kapatılıyor (not implemented yet)');
  };

  const joinTicket = async (_ticketId: string): Promise<void> => {
    if (!currentPlayer) return;
    // TODO: SarLab live support API entegrasyonu
    console.log('Ticket\'a katılınıyor (not implemented yet)');
  };

  const leaveTicket = () => {
    // TODO: SarLab live support API entegrasyonu
    console.log('Ticket\'tan ayrılınıyor (not implemented yet)');
  };

  const adminCloseTicket = async (_ticketId: string): Promise<void> => {
    // TODO: SarLab live support API entegrasyonu
    console.log('Admin ticket kapatıyor (not implemented yet)');
  };

  const value = {
    activeTicket,
    tickets,
    messages,
    isConnected: !!currentPlayer,
    isLoadingTicket,
    createTicket,
    sendSupportMessage,
    closeTicket,
    joinTicket,
    leaveTicket,
    adminCloseTicket
  };

  return (
    <LiveSupportContext.Provider value={value}>
      {children}
    </LiveSupportContext.Provider>
  );
};
